import React, {useState, useRef, useEffect} from 'react';
import Header from '../layouts/Header';
import {logger} from '../../services/utils.service';
import {useNavigate} from 'react-router';
import {handleApiResponse, leadConversion, logJsError} from '../../services/api.service';
import {snippetService} from '../../services/snippet.service';
import PrivacyPolicy from '../common/Privacy-policy';
import LeadFormFields from './Lead-form-fields';
import {CONVERSION_TYPE} from '../../models/conversion-type.enum';
import {Lead} from '../../models/lead.model';
import {GENERIC_ERROR_MESSAGE} from '../../models/system.model';

const LeadForm = ({isEmbedded}: {isEmbedded: boolean}) => {
	const navigate = useNavigate();
	const leadFormRef = useRef<HTMLDivElement>(null);
	const [formData, setFormData] = useState({firstName: '', lastName: '', email: '', phone: ''});
	const [successMessage, setSuccessMessage] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	useEffect(() => {
		leadFormRef.current?.scrollIntoView({block: 'end', behavior: 'smooth'});
	}, []);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const {name, value} = event.target;
		setFormData({...formData, [name]: value});
	};

	const resetForm = (): void => {
		setFormData({firstName: '', lastName: '', email: '', phone: ''});
	};

	const handleConversionSuccess = (lead: Lead): void => {
		setSuccessMessage(true);
		snippetService.sendConversionMessage(lead, CONVERSION_TYPE.FORM);
		resetForm();
		logger('Submitted lead: ', lead);
	};

	const handleConversionError = (error: Error): void => {
		logJsError(error);
		setErrorMessage(GENERIC_ERROR_MESSAGE);
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const lead: Lead = {
			first_name: formData.firstName,
			last_name: formData.lastName,
			phone: formData.phone,
			email: formData.email,
		};

		leadConversion(lead)
			.then(handleApiResponse)
			.then(() => {
				handleConversionSuccess(lead);
			})
			.catch((error) => {
				handleConversionError(error);
			});
	};

	return (
		<>
			{!isEmbedded && <Header/>}
			<div ref={leadFormRef}
			     className={`bg-white h-full z-10 h-100 flex flex-col ${isEmbedded ? 'border rounded-md shadow-md' : 'rounded-b-md'}`}>
				{!successMessage ? (
					<form onSubmit={handleSubmit} className="p-4 relative grow flex flex-col">
						<h2 className="text-2xl font-medium">Get in touch</h2>
						<div className="text-gray-500 text-xs mb-2">Fill in your contact information and one of our
							experts will reach out to you shortly.
						</div>

						<LeadFormFields formData={formData} onChange={handleChange}/>

						<PrivacyPolicy/>

						<div className="flex justify-end">
							<button onClick={() => {navigate(-1)}} type="button"
							        className="bg-white text-gray-700 px-4 py-2 rounded-md focus:outline-hidden
							        focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 mr-[16px]">Cancel
							</button>
							<button type="submit"
							        className="bg-primary text-white px-4 py-2 rounded-md focus:outline-hidden
							        focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50">Submit
							</button>
						</div>
						{errorMessage && <div className="text-center text-red-500 text-sm p-4">{errorMessage}</div>}
					</form>
				) : (
					<div className="flex items-center justify-center h-full text-center">
						<div className="w-80">
							<div>Your request has been received!</div>
							<div>Our team will be in touch with you soon. Thank you for reaching out!</div>
							<div className="mt-4">
								<a className="text-blue-500 underline hover:text-blue-700 cursor-pointer"
								   onClick={() => {navigate(-1)}}>Back to chat</a>
							</div>
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export default LeadForm;
